/**
 * Resume from Clarification for Gibbs Text-to-Action
 *
 * Merges a user's clarification answer back into a partial action plan,
 * re-validates, and returns either a valid plan or another clarification.
 *
 * @module gibbs/text-to-action/resumeFromClarification
 */

import { getAction, checkPreconditions } from '../registry/index';
import { generateClarification } from './clarification';
import {
    buildWorkflowForSelectedTarget,
    buildWorkflowForStructureSource,
} from '../knowledge';
import {
    buildTargetSelectionClarification,
    buildGenericTargetClarification,
} from './anchorClarifications';
import { resolve as resolveAnchor } from './anchorResolver';
import { diseaseTargets } from '../knowledge/diseaseTargets';
import { rewriteWithDisambiguation } from '../knowledge/chemistryDisambiguation';
import { buildDockAndScoreWorkflow } from '../knowledge/dockAndScoreShortcut';

// ─── Parameter validation helpers ──────────────────────────────────────────

/**
 * Validate a single parameter value against its ParamDef.
 *
 * @param {*} value - The value to validate
 * @param {import('../registry/schema.js').ParamDef} paramDef
 * @returns {{ valid: boolean, reason?: string }}
 */
function validateParamValue(value, paramDef) {
    if (value == null) {
        return paramDef.required
            ? { valid: false, reason: `${paramDef.name} is required` }
            : { valid: true };
    }

    // Enum check
    if (paramDef.options && paramDef.options.length > 0) {
        if (!paramDef.options.includes(value)) {
            return { valid: false, reason: `${paramDef.name} must be one of: ${paramDef.options.join(', ')}` };
        }
    }

    // Constraint checks
    if (paramDef.constraints) {
        const { min, max, pattern } = paramDef.constraints;
        if (min != null && typeof value === 'number' && value < min) {
            return { valid: false, reason: `${paramDef.name} must be at least ${min}` };
        }
        if (max != null && typeof value === 'number' && value > max) {
            return { valid: false, reason: `${paramDef.name} must be at most ${max}` };
        }
        if (pattern && typeof value === 'string' && !new RegExp(pattern).test(value)) {
            return { valid: false, reason: `${paramDef.name} does not match required format` };
        }
    }

    return { valid: true };
}

/**
 * Find all missing required parameters for an action given current params.
 *
 * @param {import('../registry/schema.js').ActionDef} actionDef
 * @param {Object} params - Currently populated parameters
 * @returns {string[]} Names of missing required params
 */
function findMissingRequired(actionDef, params) {
    const missing = [];
    const allDefs = [...actionDef.inputs, ...actionDef.parameters];

    for (const paramDef of allDefs) {
        if (!paramDef.required) continue;
        const value = params[paramDef.name];
        if (value == null && paramDef.defaultValue == null) {
            missing.push(paramDef.name);
        }
    }

    return missing;
}

/**
 * Find all invalid parameters for an action given current params.
 *
 * @param {import('../registry/schema.js').ActionDef} actionDef
 * @param {Object} params
 * @returns {{ name: string, reason: string }[]}
 */
function findInvalidParams(actionDef, params) {
    const invalid = [];
    const allDefs = [...actionDef.inputs, ...actionDef.parameters];

    for (const paramDef of allDefs) {
        const value = params[paramDef.name];
        if (value == null) continue; // missing is handled separately
        const result = validateParamValue(value, paramDef);
        if (!result.valid) {
            invalid.push({ name: paramDef.name, reason: result.reason });
        }
    }

    return invalid;
}

/**
 * Apply default values to params where not specified.
 *
 * @param {import('../registry/schema.js').ActionDef} actionDef
 * @param {Object} params
 * @returns {Object} params with defaults applied
 */
function applyDefaults(actionDef, params) {
    const result = { ...params };
    const allDefs = [...actionDef.inputs, ...actionDef.parameters];

    for (const paramDef of allDefs) {
        if (result[paramDef.name] == null && paramDef.defaultValue != null) {
            result[paramDef.name] = paramDef.defaultValue;
        }
    }

    return result;
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Merge a user's clarification answer into a partial action plan and re-validate.
 *
 * @param {Object} clarificationContext - The context from a previous clarification
 * @param {Object} clarificationContext.actionId - The target action ID
 * @param {Object} clarificationContext.partialParams - Previously extracted params
 * @param {string[]} [clarificationContext.unmetPreconditions] - Previously unmet preconditions
 * @param {*} userAnswer - The user's selected option value or typed answer
 * @param {Object} workspaceState - Current serialized workspace state
 * @returns {{ plan: Object|null, needsMoreClarification: Object|null }}
 */
export function resumeFromClarification(clarificationContext, userAnswer, workspaceState) {
    // ─── Diagnostic logging ───────────────────────────────────────────
    // Every resume attempt logs its context so that, when the flow
    // produces the "could not find the action to resume" error or
    // routes to an unexpected action, we can see exactly what was in
    // the clarification context at the time.
    // eslint-disable-next-line no-console
    console.info('[Gibbs] resumeFromClarification called:', {
        clarificationContext,
        userAnswer,
        userAnswerType: Array.isArray(userAnswer)
            ? 'array'
            : typeof userAnswer,
    });

    // Disease cascade short-circuit: if the clarification context was from
    // the disease-goal matcher (L0 → L1 or L1 → L2 stages), dispatch to the
    // cascade resolver instead of the regular action-param resume path.
    if (clarificationContext?.cascade === 'disease_target_selection') {
        return resumeDiseaseCascade(clarificationContext, userAnswer);
    }

    // Campaign-verb cascade: the user selected one of the starter actions
    // offered when their query was a campaign-level goal (e.g., "lead
    // design campaign"). Build a single-action plan for the chosen action.
    // The clarify options carry bare actionId strings as values, so
    // userAnswer can be either the string directly (from the Chip click
    // handler) or an object { value: actionId } from a typed response.
    if (clarificationContext?.cascade === 'campaign_verb') {
        return resumeCampaignCascade(clarificationContext, userAnswer, workspaceState);
    }

    // ── New cascades from gibbs-clarify-on-ambiguous-target ──────────
    if (clarificationContext?.cascade === 'target_confirmation') {
        return resumeTargetConfirmationCascade(clarificationContext, userAnswer, workspaceState);
    }
    if (clarificationContext?.cascade === 'target_selection') {
        return resumeTargetSelectionCascade(clarificationContext, userAnswer, workspaceState);
    }
    if (clarificationContext?.cascade === 'generic_target') {
        return resumeGenericTargetCascade(clarificationContext, userAnswer, workspaceState);
    }

    // ── Destructive-confirm cascade (gibbs-destructive-ops-ux, 2026-05-03)
    // The chain emits a clarify when the user's plan contains an
    // ActionDef with `destructiveConfirm: true`. The clarify's context
    // carries the staged plan. On 'confirm', re-emit the staged plan as
    // a `plan` result so the executor dispatches it. On 'cancel', emit a
    // chat acknowledgment with no dispatch.
    if (clarificationContext?.cascade === 'destructive_confirm') {
        return resumeDestructiveConfirmCascade(clarificationContext, userAnswer);
    }

    // ── Chemistry-disambiguation cascade (gibbs-chemistry-disambiguation,
    // 2026-05-15; Theme F from Bill Kinney walkthrough 2026-05-14).
    // The detector emits a clarify when the user references an ambiguous
    // chemistry term ("alpha hydrogen" on a compound with multiple
    // functional groups). The user's pick is a `{ rewrite, ruleId }`
    // object; the resume rewrites the original message with the
    // disambiguated phrasing and signals re-dispatch.
    if (clarificationContext?.cascade === 'chemistry_disambiguation') {
        return resumeChemistryDisambiguationCascade(clarificationContext, userAnswer);
    }

    // Defensive null-check on the context itself. Missing context at this
    // point usually means the LLM returned a clarify intent without
    // `context` populated, which would have broken every subsequent
    // resume attempt.
    if (!clarificationContext || typeof clarificationContext !== 'object') {
        // eslint-disable-next-line no-console
        console.warn(
            '[Gibbs] resumeFromClarification: null or invalid context.'
            + ' The original clarify intent likely did not include a'
            + ' context.actionId field. Check the LLM output format.'
        );
        return {
            plan: null,
            needsMoreClarification: {
                intent: 'clarify',
                question: 'I lost track of what we were clarifying. Could you rephrase your original request?',
                options: [],
                missing: [],
                context: { actionId: null, partialParams: {} },
            },
        };
    }

    const { actionId, partialParams = {} } = clarificationContext;

    // If the user selected a resolution action (from precondition clarification),
    // return a plan to execute that resolution action instead
    if (userAnswer && typeof userAnswer === 'object' && userAnswer.resolveAction) {
        const resolveAction = getAction(userAnswer.resolveAction);
        if (resolveAction) {
            return {
                plan: {
                    intent: 'single_action',
                    actions: [{ actionId: userAnswer.resolveAction, params: {} }],
                    followUp: { actionId, partialParams },
                },
                needsMoreClarification: null,
            };
        }
        // eslint-disable-next-line no-console
        console.warn(
            '[Gibbs] resumeFromClarification: userAnswer.resolveAction'
            + ` "${userAnswer.resolveAction}" is not a registered action.`
            + ' Check PRECONDITION_RESOLUTIONS in clarification.js.'
        );
    }

    // If the user selected from an ambiguous intent clarification
    if (userAnswer && typeof userAnswer === 'object' && userAnswer.actionId) {
        const selectedAction = getAction(userAnswer.actionId);
        if (selectedAction) {
            // Re-check with the now-known action
            return resumeWithAction(selectedAction, partialParams, workspaceState);
        }
    }

    // Standard case: merge the answer into partial params
    const actionDef = getAction(actionId);
    if (!actionDef) {
        // eslint-disable-next-line no-console
        console.warn(
            '[Gibbs] resumeFromClarification: context.actionId'
            + ` "${actionId}" is not a registered action. This usually`
            + ' means the original LLM clarify response was missing'
            + ' a context.actionId field (the LLM should set it to the'
            + ' action whose parameters it is asking about). Surfacing'
            + ' "could not find the action to resume" to the user.'
        );
        return {
            plan: null,
            needsMoreClarification: {
                intent: 'clarify',
                question: 'I could not find the action to resume. Could you start over?',
                options: [],
                missing: [],
                context: { actionId: null, partialParams },
            },
        };
    }

    // Determine which parameter the answer fills.
    // Use the missing list from context if available, otherwise infer.
    const missing = clarificationContext.missing || findMissingRequired(actionDef, partialParams);
    const mergedParams = { ...partialParams };

    if (missing.length > 0) {
        // Assign the answer to the first missing parameter
        mergedParams[missing[0]] = userAnswer;
    } else if (typeof userAnswer === 'object' && userAnswer !== null && !Array.isArray(userAnswer)) {
        // Bulk merge if the answer is an object with param names
        Object.assign(mergedParams, userAnswer);
    }

    return resumeWithAction(actionDef, mergedParams, workspaceState);
}

/**
 * Validate merged params against an action and return plan or further clarification.
 *
 * @param {import('../registry/schema.js').ActionDef} actionDef
 * @param {Object} mergedParams
 * @param {Object} workspaceState
 * @returns {{ plan: Object|null, needsMoreClarification: Object|null }}
 */
function resumeWithAction(actionDef, mergedParams, workspaceState) {
    // Apply defaults
    const params = applyDefaults(actionDef, mergedParams);

    // Check preconditions
    const precondResult = checkPreconditions(actionDef.id, workspaceState);
    if (!precondResult.met) {
        return {
            plan: null,
            needsMoreClarification: generateClarification(
                actionDef, [], workspaceState, params
            ),
        };
    }

    // Check for invalid params
    const invalid = findInvalidParams(actionDef, params);
    if (invalid.length > 0) {
        const invalidNames = invalid.map((i) => i.name);
        return {
            plan: null,
            needsMoreClarification: generateClarification(
                actionDef, invalidNames, workspaceState, params
            ),
        };
    }

    // Check for still-missing required params
    const stillMissing = findMissingRequired(actionDef, params);
    if (stillMissing.length > 0) {
        return {
            plan: null,
            needsMoreClarification: generateClarification(
                actionDef, stillMissing, workspaceState, params
            ),
        };
    }

    // All good -- return a valid action plan
    return {
        plan: {
            intent: 'single_action',
            actions: [{
                actionId: actionDef.id,
                params,
            }],
        },
        needsMoreClarification: null,
    };
}

// ─── Campaign-Verb Cascade Resumption ──────────────────────────────────────

/**
 * Dispatch a clarification answer that came from the campaign-verb cascade.
 * The context's `options` (resolved by the caller) carries registry
 * actionIds as values; the user's answer identifies which starter action
 * they chose. Build a single-action plan for that action, applying
 * defaults and re-checking preconditions the same way `resumeWithAction`
 * does for normal clarifications.
 *
 * @param {Object} context
 * @param {string|Object} userAnswer - bare actionId string, or {value: id}
 * @param {Object} workspaceState
 * @returns {{ plan: Object|null, needsMoreClarification: Object|null }}
 */
function resumeCampaignCascade(context, userAnswer, workspaceState) {
    const chosenId = typeof userAnswer === 'string'
        ? userAnswer
        : (userAnswer && userAnswer.value) || null;

    if (!chosenId) {
        return {
            plan: null,
            needsMoreClarification: {
                intent: 'clarify',
                question: 'I did not get which option you picked. Could you try again?',
                options: [],
                missing: [],
                context: { ...context },
            },
        };
    }

    const actionDef = getAction(chosenId);
    if (!actionDef) {
        // eslint-disable-next-line no-console
        console.warn(
            `[Gibbs] resumeCampaignCascade: chosen actionId "${chosenId}" is`
            + ' not registered. Campaign catalog entry may reference a stale'
            + ' action; the action-reference audit should have caught this.'
        );
        return {
            plan: null,
            needsMoreClarification: {
                intent: 'clarify',
                question: 'That action is no longer available. Could you restart your request?',
                options: [],
                missing: [],
                context: { cascade: null, partialParams: {} },
            },
        };
    }

    return resumeWithAction(actionDef, {}, workspaceState);
}

// ─── Disease Cascade Resumption ────────────────────────────────────────────

/**
 * Dispatch a clarification answer that came from the disease-goal cascade.
 * The context's `stage` field determines which step of the cascade this is:
 *
 *   'L0_to_L1' — user picked a drug target from the disease's target list.
 *                Build a workflow plan (or a nested L1→L2 clarification if
 *                the chosen target has no experimental structure).
 *
 *   'L1_to_L2' — user picked a structure acquisition path. Build the
 *                final workflow plan.
 *
 * @param {Object} context - The clarification context with cascade === 'disease_target_selection'
 * @param {Object} userAnswer - The selected option value object
 * @returns {{ plan: Object|null, needsMoreClarification: Object|null }}
 */
function resumeDiseaseCascade(context, userAnswer) {
    const { stage, diseaseSlug, workflowIntent } = context;

    if (stage === 'L0_to_L1') {
        // User picked a target. Build a workflow for that target —
        // or a nested clarification if no experimental structure exists.
        // 2026-06-02: workflowIntent (carried through from the user's
        // original message via the clarification context) lets us emit
        // the full template plan with loadPdbId pre-filled, saving the
        // user from re-prompting after acquire.
        const targetIdx = userAnswer?.targetIdx ?? -1;
        // Per-option workflowIntent override (in case different options
        // ever want different downstream workflows). Falls back to the
        // context-level value, which is what the cascade emits today.
        const intent = (userAnswer && userAnswer.workflowIntent)
            || workflowIntent
            || null;
        const result = buildWorkflowForSelectedTarget(
            diseaseSlug, targetIdx, intent,
        );
        return wrapCascadeResult(result);
    }

    if (stage === 'L1_to_L2') {
        // User picked a structure source for a target that lacked a PDB.
        const targetIdx = context.targetIdx ?? userAnswer?.targetIdx ?? -1;
        const structureSource = userAnswer?.structureSource || 'abort';
        const result = buildWorkflowForStructureSource(
            diseaseSlug, targetIdx, structureSource
        );
        return wrapCascadeResult(result);
    }

    return {
        plan: null,
        needsMoreClarification: {
            intent: 'clarify',
            question: 'I lost track of the cascade stage. Could you restart your request?',
            options: [],
            missing: [],
            context: {},
        },
    };
}

/**
 * Wrap a cascade result into the shape that the rest of the pipeline
 * expects. The cascade can return workflow, clarify, or chat intents;
 * this normalizes them into {plan, needsMoreClarification, chatMessage}.
 *
 * - workflow / single_action → plan
 * - clarify → needsMoreClarification
 * - chat → chatMessage (new — the caller should addBotMessage this)
 *
 * @param {Object} result
 * @returns {{
 *   plan: Object|null,
 *   needsMoreClarification: Object|null,
 *   chatMessage: string|null
 * }}
 */
function wrapCascadeResult(result) {
    if (!result) {
        return { plan: null, needsMoreClarification: null, chatMessage: null };
    }
    if (result.intent === 'workflow' || result.intent === 'single_action') {
        return { plan: result, needsMoreClarification: null, chatMessage: null };
    }
    if (result.intent === 'clarify') {
        return { plan: null, needsMoreClarification: result, chatMessage: null };
    }
    if (result.intent === 'chat') {
        return {
            plan: null,
            needsMoreClarification: null,
            chatMessage: result.message || '',
        };
    }
    return { plan: null, needsMoreClarification: null, chatMessage: null };
}

// ─── target_confirmation cascade resume ──────────────────────────────────────
//
// Three options:
//   use_candidate     → anchor on the candidate; return a "confirmed anchor"
//                       result that the routing chain can carry back into
//                       the LLM workflow tier with the protein in scope.
//   choose_different  → emit a target_selection clarify for the same domain.
//   tell_more         → emit a chat message describing the candidate's role.

// eslint-disable-next-line no-unused-vars -- uniform cascade-resume signature
function resumeTargetConfirmationCascade(context, userAnswer, _workspaceState) {
    const action = (userAnswer && (userAnswer.action || userAnswer))
        || (typeof userAnswer === 'string' ? userAnswer : null);

    if (action === 'use_candidate') {
        // 2026-06-01 (G2 RCA fix, related-note path): same defect as
        // resumeTargetSelectionCascade — previously returned an
        // `anchored:` field that no UI handler consumes, so the user
        // saw "I could not resume from that choice." after clicking
        // "Use this candidate". Now: emit a dispatchable workflow plan
        // directly via buildDockAndScoreWorkflow.
        const candidate = context.candidate || {};
        const pdbId = candidate.pdbId
            || (Array.isArray(candidate.pdbIds) && candidate.pdbIds[0])
            || null;
        if (pdbId) {
            const entry = diseaseTargets && diseaseTargets.diseases
                && diseaseTargets.diseases[context.domainSlug];
            const domainLabel = (entry && entry.canonicalName) || context.domainSlug;
            const targetLabel = `${candidate.geneSymbol || candidate.name || 'the target'}`
                + ` (${domainLabel})`;
            const plan = buildDockAndScoreWorkflow({
                phase: 'acquire',
                loadPdbId: pdbId,
                targetLabel,
            });
            return {
                plan,
                needsMoreClarification: null,
                chatMessage: null,
            };
        }
        // No PDB available — friendly chat fallback (don't strand the user)
        return {
            plan: null,
            needsMoreClarification: null,
            chatMessage: `I picked ${candidate.name || candidate.geneSymbol || 'that target'}`
                + ' but it has no PDB structure I can load directly. Try'
                + ' specifying a PDB ID, or ask for a folded model.',
        };
    }

    if (action === 'choose_different') {
        const next = buildTargetSelectionClarification(context.domainSlug);
        return { plan: null, needsMoreClarification: next, chatMessage: null };
    }

    if (action === 'tell_more') {
        const entry = diseaseTargets && diseaseTargets.diseases
            && diseaseTargets.diseases[context.domainSlug];
        const domain = (entry && entry.canonicalName) || context.domainSlug;
        const candidate = context.candidate;
        const candidateLabel = (candidate && (candidate.name || candidate.pdbId))
            || 'this protein';
        // Try to surface a maturityNote for the matched topTarget if available.
        let extra = '';
        if (entry && Array.isArray(entry.topTargets)) {
            const t = entry.topTargets.find((tt) => {
                const cand = (candidate && (candidate.pdbId || candidate.name)) || '';
                if (!cand) return false;
                const candUpper = cand.toUpperCase();
                if (tt.uniprot && tt.uniprot.toUpperCase() === candUpper) return true;
                if (Array.isArray(tt.pdbIds)
                    && tt.pdbIds.some((p) => p && p.toUpperCase() === candUpper)) return true;
                return false;
            });
            if (t && t.maturityNote) extra = `\n\n${t.maturityNote}`;
        }
        return {
            plan: null,
            needsMoreClarification: null,
            chatMessage: `${candidateLabel} is associated with ${domain}.${extra}`,
            // Re-emit the original confirmation so the user can answer
            // use/choose-different after reading.
            pending: context,
        };
    }

    // Unrecognized answer — re-emit the original confirmation
    return { plan: null, needsMoreClarification: null, chatMessage: null };
}

// ─── target_selection cascade resume ─────────────────────────────────────────
//
// User picked one of the listed topTargets. Anchor on that target.

// eslint-disable-next-line no-unused-vars -- uniform cascade-resume signature
function resumeTargetSelectionCascade(context, userAnswer, _workspaceState) {
    // userAnswer typically carries `value: { cascade, domainSlug, target }`.
    const v = (userAnswer && userAnswer.value) || userAnswer || {};
    const target = v.target || (typeof userAnswer === 'object' && userAnswer.target) || null;
    // 2026-06-01 (G2 RCA fix): previously returned `anchored: {...}` here
    // expecting the routing chain to consume it on a later protocol step.
    // That consumer was never wired (ChatBot.handleClarificationSelect
    // only destructures {plan, needsMoreClarification, chatMessage,
    // rewriteMessage} — the `anchored` field was dropped on the floor),
    // so every target-selection pill click fell through to the literal
    // "I could not resume from that choice." message. Now: emit a
    // dispatchable workflow plan directly via buildDockAndScoreWorkflow,
    // matching the canonical hit-to-lead acquisition path used by the
    // disease cascade's own L1→workflow builder
    // (diseaseCascade.buildAcquireStructureWorkflowFromPdb).
    if (target && (target.pdbId || target.uniprot || target.name)) {
        const pdbId = target.pdbId
            || (Array.isArray(target.pdbIds) && target.pdbIds[0])
            || null;
        if (pdbId) {
            const entry = diseaseTargets && diseaseTargets.diseases
                && diseaseTargets.diseases[context.domainSlug];
            const domainLabel = (entry && entry.canonicalName) || context.domainSlug;
            const targetLabel = `${target.geneSymbol || target.name || 'the target'}`
                + ` (${domainLabel})`;
            const plan = buildDockAndScoreWorkflow({
                phase: 'acquire',
                loadPdbId: pdbId,
                targetLabel,
            });
            return {
                plan,
                needsMoreClarification: null,
                chatMessage: null,
            };
        }
        // Target has no PDB id we can load — fall back to a friendly chat
        // explaining the gap rather than the cryptic "could not resume"
        // string. The user can still pursue the same target via a manual
        // load step.
        return {
            plan: null,
            needsMoreClarification: null,
            chatMessage: `I picked ${target.name || target.geneSymbol || 'that target'} `
                + 'but it has no PDB structure I can load directly. Try '
                + 'specifying a PDB ID, or ask for a folded model.',
        };
    }
    // Defensive — unrecognized answer falls back to re-emitting the clarify
    return {
        plan: null,
        needsMoreClarification: buildTargetSelectionClarification(context.domainSlug),
        chatMessage: null,
    };
}

// ─── generic_target cascade resume ───────────────────────────────────────────
//
// Two options:
//   type_target  → user typed a free-form target; re-run the resolver
//                  with that input as the new current message.
//   pick_disease → emit a follow-up clarify listing diseases.

function resumeGenericTargetCascade(_context, userAnswer, workspaceState) {
    const v = (userAnswer && userAnswer.value) || userAnswer || {};
    const action = (v && v.action) || (typeof userAnswer === 'string' ? userAnswer : null);

    if (action === 'type_target') {
        const typed = (v && typeof v.input === 'string' ? v.input : '')
            || (typeof userAnswer === 'string' ? userAnswer : '');
        if (!typed.trim()) {
            // Empty / garbage input — re-clarify
            return {
                plan: null,
                needsMoreClarification: buildGenericTargetClarification(),
                chatMessage: 'I didn\'t catch a target name there — try again.',
            };
        }
        const result = resolveAnchor(typed, [], workspaceState);
        if (result.status === 'anchored') {
            return {
                plan: null,
                needsMoreClarification: null,
                chatMessage: null,
                anchored: {
                    cascade: 'generic_target',
                    candidate: result.protein,
                    source: result.source,
                },
            };
        }
        // Still ambiguous — re-clarify rather than silently fail
        return {
            plan: null,
            needsMoreClarification: buildGenericTargetClarification(),
            chatMessage: 'I couldn\'t resolve that target — try a PDB ID or '
                + 'pick a disease below.',
        };
    }

    // 2026-06-01 (G1 RCA fix): check `v.domainSlug` BEFORE the bare
    // `action === 'pick_disease'` branch. The pill payload emitted by the
    // follow-up clarify below carries BOTH `action: 'pick_disease'` AND
    // `domainSlug: <s>`. Previously the action-only branch matched first
    // and re-emitted the same disease list, so the user's pill click
    // looped them back to the same clarification (G1 — "disease cascade
    // clarification loop"). The domainSlug-bearing payload is by
    // construction a second-stage answer; it gets routed into target
    // selection here. The bare-action `pick_disease` (from
    // buildGenericTargetClarification's "Pick a disease" option in
    // anchorClarifications.js:123-142) reaches the next branch below.
    if (v && v.domainSlug) {
        // Second-stage of pick_disease — user picked a disease.
        return {
            plan: null,
            needsMoreClarification: buildTargetSelectionClarification(v.domainSlug),
            chatMessage: null,
        };
    }

    if (action === 'pick_disease') {
        // Emit a follow-up clarify listing top diseases. Keeping this small
        // to avoid a 55-option dropdown — a few common headliners + a free
        // type-it-in fallback. The pill payload here carries
        // `action: 'pick_disease'` AND `domainSlug: <s>` — the domainSlug
        // branch above will route into target_selection on the next click.
        const headliners = ['breast_cancer', 'hepatitis_b', 'type_2_diabetes',
            'multiple_sclerosis', 'copd', 'major_depression'];
        const opts = headliners
            .filter((s) => diseaseTargets && diseaseTargets.diseases
                && diseaseTargets.diseases[s])
            .map((s) => ({
                label: diseaseTargets.diseases[s].canonicalName || s,
                value: { cascade: 'generic_target', action: 'pick_disease', domainSlug: s },
            }));
        return {
            plan: null,
            needsMoreClarification: {
                intent: 'clarify',
                cascade: 'generic_target',
                question: 'Pick a disease — or type a target if you want a specific protein.',
                options: opts,
                context: { cascade: 'generic_target', stage: 'disease_pick' },
            },
            chatMessage: null,
        };
    }

    // Unrecognized — re-clarify
    return {
        plan: null,
        needsMoreClarification: buildGenericTargetClarification(),
        chatMessage: null,
    };
}

/**
 * Resume the destructive-confirm cascade
 * (gibbs-destructive-ops-ux, 2026-05-03).
 *
 * Turn 1 emitted a clarify with `cascade: 'destructive_confirm'` and a
 * staged plan in context. Turn 2's userAnswer is one of:
 *   - `{ decision: 'confirm' }` — re-emit the staged plan as a `plan`
 *     result so the executor dispatches it.
 *   - `{ decision: 'cancel' }` — emit a chat acknowledgment with no
 *     dispatch.
 *
 * If userAnswer is unrecognized (e.g., a string typed back instead of a
 * structured option click), default to cancel — fail safe for a
 * destructive action.
 *
 * @param {Object} context - clarification context
 *   ({ cascade: 'destructive_confirm', actionId, stagedPlan, stagedTier,
 *     originalMessage })
 * @param {*} userAnswer - the user's option value
 * @returns {{
 *   plan: Object|null,
 *   needsMoreClarification: Object|null,
 *   chatMessage: string|null,
 *   tier?: string,
 * }}
 */
function resumeDestructiveConfirmCascade(context, userAnswer) {
    const v = (userAnswer && userAnswer.value) || userAnswer || {};
    const decision = (v && v.decision) || (typeof userAnswer === 'string' ? userAnswer : null);

    if (decision === 'confirm') {
        return {
            plan: context.stagedPlan,
            needsMoreClarification: null,
            chatMessage: null,
            tier: context.stagedTier,
        };
    }

    // Default (cancel or unrecognized) — fail safe.
    return {
        plan: null,
        needsMoreClarification: null,
        chatMessage: 'OK — cancelled, nothing was dropped.',
    };
}

// ─── chemistry_disambiguation cascade resume ─────────────────────────────────
//
// The user picked one of the listed candidate interpretations. The context
// carries the original message + the rule that fired. The picked option's
// `value` carries the rewrite phrase. Rewrite the original message and
// return a `rewriteMessage` field so the chain re-dispatches the new text.
//
// We do not return a `plan` directly — the rewritten message has to go
// through the chain again so that whatever path was originally intended
// (shortcut, LLM tier, etc.) re-runs against the disambiguated query.

/**
 * @param {Object} context - { cascade, ruleId, originalMessage }
 * @param {*} userAnswer - { rewrite, ruleId, candidateId } or {value: ...}
 * @returns {{ plan: null, needsMoreClarification: null, chatMessage: null,
 *   rewriteMessage: string }}
 */
function resumeChemistryDisambiguationCascade(context, userAnswer) {
    const v = (userAnswer && userAnswer.value) || userAnswer || {};
    const rewrite = (v && v.rewrite) || null;
    const ruleId = (v && v.ruleId) || context.ruleId;
    const original = context.originalMessage || '';

    if (!rewrite || !ruleId || !original) {
        // Unrecognized answer — fall through to chat. The original
        // message was lost, so we cannot re-run it; tell the user.
        return {
            plan: null,
            needsMoreClarification: null,
            chatMessage: 'I lost the context for that clarification. '
                + 'Could you rephrase your original request?',
        };
    }

    const rewritten = rewriteWithDisambiguation(original, ruleId, rewrite);
    return {
        plan: null,
        needsMoreClarification: null,
        chatMessage: null,
        rewriteMessage: rewritten,
    };
}
